import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { supabase } from '@/services/supabase';
import { Card, CardBody } from '@/components/common/Card';
import DataTable from '@/components/common/DataTable';
import SearchInput from '@/components/common/SearchInput';
import Badge from '@/components/common/Badge';
import { formatDate, formatTimeAgo } from '@/utils/formatters';

interface WaitlistEntry {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  referral_source: string | null;
  status: string;
  notes: string | null;
  created_at: string;
}

export default function Waitlist() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['waitlist', page, search],
    queryFn: async () => {
      let query = supabase
        .from('waitlist')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range((page - 1) * 20, page * 20 - 1);

      if (search) {
        query = query.or(`email.ilike.%${search}%,name.ilike.%${search}%`);
      }

      const { data, count, error } = await query;
      if (error) throw error;
      return { entries: data as WaitlistEntry[], total: count || 0 };
    },
  });

  const columns = useMemo<ColumnDef<WaitlistEntry>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-gray-900">{row.original.email}</p>
            {row.original.name && (
              <p className="text-xs text-gray-500">{row.original.name}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        cell: ({ row }) => row.original.phone || '-',
      },
      {
        accessorKey: 'location',
        header: 'Location',
        cell: ({ row }) => {
          const city = row.original.city;
          const state = row.original.state;
          if (!city && !state) return '-';
          return `${city || ''}${city && state ? ', ' : ''}${state || ''}`;
        },
      },
      {
        accessorKey: 'referral_source',
        header: 'Source',
        cell: ({ row }) => row.original.referral_source || '-',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          const variant = status === 'invited' ? 'success' : status === 'pending' ? 'info' : 'gray';
          return <Badge variant={variant}>{status}</Badge>;
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Joined',
        cell: ({ row }) => (
          <div>
            <p className="text-gray-900">{formatDate(row.original.created_at)}</p>
            <p className="text-xs text-gray-500">{formatTimeAgo(row.original.created_at)}</p>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Waitlist</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage waitlist entries and invitations
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Total Entries</p>
            <p className="text-2xl font-bold">{data?.total || 0}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold">
              {data?.entries.filter((e) => e.status === 'pending').length || 0}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <p className="text-sm text-gray-500">Invited</p>
            <p className="text-2xl font-bold">
              {data?.entries.filter((e) => e.status === 'invited').length || 0}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardBody padding="sm">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by email or name..."
            className="max-w-md"
          />
        </CardBody>
      </Card>

      {/* Waitlist table */}
      <Card>
        <CardBody padding="none">
          <DataTable
            data={data?.entries || []}
            columns={columns}
            isLoading={isLoading}
            page={page}
            pageSize={20}
            totalCount={data?.total}
            onPageChange={setPage}
          />
        </CardBody>
      </Card>
    </div>
  );
}
